import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { resource } from "../../util/resource";
import { DWS } from "../../interfaces/dws";
import { Preorder } from "../../interfaces/dws/preorder";
import PreorderOrderProduct = Preorder.PreorderOrderProduct;
import { map } from "rxjs/operators";
import * as moment from "moment";

/** Inclusive calendar day (local), matching the date the range picker shows. */
export function calendarDay(d: unknown): string | undefined {
  if (d === undefined || d === null || d === "") {
    return undefined;
  }
  if (typeof d === "object" && !(d instanceof Date) && !moment.isMoment(d)) {
    const range = d as { start?: unknown; end?: unknown };
    if ("start" in range || "end" in range) {
      return calendarDay(range.start ?? range.end);
    }
  }
  const m = moment(d as any);
  return m.isValid() ? m.clone().startOf("day").format("YYYY-MM-DD") : undefined;
}

/**
 * One filled side of the range means that day only.
 * The picker shows `9/18/2026 –` when the end is left empty; that must not stay an open-ended query.
 */
function closedDayRange(start: unknown, end: unknown): { start?: string; end?: string } {
  const from = calendarDay(start);
  const to = calendarDay(end);
  if (from && !to) {
    return { start: from, end: from };
  }
  if (to && !from) {
    return { start: to, end: to };
  }
  return { start: from, end: to };
}

/** Local calendar day of a list timestamp, same day the grid prints. */
export function displayedCalendarDay(value: unknown): string | undefined {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const m = typeof value === "number" ? moment(value) : moment.utc(value as any);
  return m.isValid() ? m.local().format("YYYY-MM-DD") : undefined;
}

function dayWithin(day: string | undefined, start?: string, end?: string): boolean {
  if (!start && !end) {
    return true;
  }
  if (!day) {
    return false;
  }
  if (start && day < start) {
    return false;
  }
  if (end && day > end) {
    return false;
  }
  return true;
}

/** Keeps a row only when every selected date bound matches the day shown in that column. */
export function selfOrderMatchesDateFilters(
  row: {
    orderDateTime?: string | number | Date | null;
    registrationDateTime?: string | number | Date | null;
    lastModifiedDateTime?: string | number | Date | null;
    preorderDate?: string | Date | null;
  },
  filters: PreorderFilters
): boolean {
  const order = closedDayRange(filters.preorderDate, filters.preorderDateEnd);
  const registration = closedDayRange(filters.registrationDate, filters.registrationDateEnd);
  const modified = closedDayRange(filters.lastModifiedDate, filters.lastModifiedDateEnd);
  const orderDay = displayedCalendarDay(row.orderDateTime) ?? displayedCalendarDay(row.preorderDate);
  const registrationDay = displayedCalendarDay(row.registrationDateTime) ?? orderDay;
  const modifiedDay = displayedCalendarDay(row.lastModifiedDateTime) ?? orderDay;
  return (
    dayWithin(orderDay, order.start, order.end) &&
    dayWithin(registrationDay, registration.start, registration.end) &&
    dayWithin(modifiedDay, modified.start, modified.end)
  );
}

/** Flat query payload for JDBC self-order list / export ({@code wineryId} is substituted into path only). */
export function buildSelfOrdersListParams(
  filters: PreorderFilters,
  page: number,
  pageSize: number
): Record<string, string | number | string[] | undefined> {
  const q: Record<string, string | number | string[] | undefined> = {
    page,
    pageSize,
    field: filters.field,
    direction: filters.direction,
  };

  const order = closedDayRange(filters.preorderDate, filters.preorderDateEnd);
  const registration = closedDayRange(filters.registrationDate, filters.registrationDateEnd);
  const modified = closedDayRange(filters.lastModifiedDate, filters.lastModifiedDateEnd);
  const odStart = order.start;
  const odEnd = order.end;
  const regStart = registration.start;
  const regEnd = registration.end;
  const lmStart = modified.start;
  const lmEnd = modified.end;

  if (filters.contactName) {
    q.contactName = filters.contactName;
  }
  if (filters.id) {
    q.id = filters.id;
  }
  if (odStart) {
    q.preorderDate = odStart;
  }
  if (odEnd) {
    q.preorderDateEnd = odEnd;
  }
  if (regStart) {
    q.registrationDate = regStart;
  }
  if (regEnd) {
    q.registrationDateEnd = regEnd;
  }
  if (lmStart) {
    q.lastModifiedDate = lmStart;
  }
  if (lmEnd) {
    q.lastModifiedDateEnd = lmEnd;
  }
  if (filters.productsQuantity !== undefined && filters.productsQuantity !== null) {
    q.productsQuantity = filters.productsQuantity;
  }
  if (
    (filters.totalGte === undefined || filters.totalGte === null) &&
    filters.total !== undefined &&
    filters.total !== null
  ) {
    q.totalGte = filters.total;
  }
  if (filters.totalGte !== undefined && filters.totalGte !== null) {
    q.totalGte = filters.totalGte;
  }
  if (filters.totalLte !== undefined && filters.totalLte !== null) {
    q.totalLte = filters.totalLte;
  }
  if (filters.channelsId?.length) {
    q.channelsId = filters.channelsId;
  }
  if (filters.state?.length) {
    q.state = filters.state as string[];
  }
  Object.keys(q).forEach((k) => {
    const v = (q as any)[k];
    if (v === undefined || v === null || v === "") {
      delete (q as any)[k];
    }
  });
  return q;
}

@Injectable({
  providedIn: "root",
})
export class PreordersService {
  constructor() {}

  list(filters: PreorderFilters, page: number = 0, pageSize: number = 999999): Observable<DWS.Page<DWS.Preorder>> {
    const listParams = buildSelfOrdersListParams(filters, page, pageSize);
    return resource("crm://self-orders/by-winery")
      .params({ wineryId: filters.wineryId, ...listParams })
      .get<DWS.Page<DWS.Preorder>>();
  }

  get(id: string): Observable<DWS.Preorder> {
    return resource("crm://preorders/id").params({ id }).get<DWS.Preorder>();
  }

  accept(id: string, body: PreorderOrderProduct): Observable<string> {
    return resource("crm://preorders/id/accept")
      .params({ id })
      .post<{ id: string }>(body)
      .pipe(map((p) => p.id));
  }

  acceptWithId(id: string, body: PreorderOrderProduct): Observable<string> {
    return resource("crm://preorders/id/accept-with-id")
      .params({ id })
      .post<{ id: string }>(body)
      .pipe(map((p) => p.id));
  }

  reject(id: string, body: PreorderOrderProduct): Observable<string> {
    console.warn(body);
    return resource("crm://preorders/id/reject")
      .params({ id })
      .post<{ id: string }>(body)
      .pipe(map((p) => p.id));
  }

  remove(id: string): Observable<void> {
    return resource("crm://preorders/id").params({ id }).delete();
  }
}

export interface PreorderFilters {
  wineryId: string;
  /** Full preorder UUID — optional filter */
  id?: string;
  contactName?: string;
  preorderDate?: string | Date;
  preorderDateEnd?: string | Date;
  registrationDate?: string | Date;
  registrationDateEnd?: string | Date;
  lastModifiedDate?: string | Date;
  lastModifiedDateEnd?: string | Date;
  productsQuantity?: number;
  /** Line total lower bound (cents), from filter EUR × 100 */
  totalGte?: number;
  /** Line total upper bound (cents) */
  totalLte?: number;
  /**
   * Single lower bound in cents (list page sets this from EUR × 100). Omitted from HTTP if {@link totalGte} is set.
   */
  total?: number;
  /** UI-only date window; stripped in listPreorder after mapping to preorderDate / preorderDateEnd */
  dateRange?: { start?: Date | string; end?: Date | string };
  channelsId?: string[];
  state?: string[];
  field: string;
  direction: "ASC" | "DESC";
}
